import { AddCommentOutlined, CloseRounded } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorIcon from '@mui/icons-material/Error'
import SaveIcon from '@mui/icons-material/Save'
import { Badge, Box, Button, CircularProgress, Collapse, Dialog, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { IApprovalRequest } from '../../definitions/Approval'
import { IFlow, INode } from '../../definitions/Nodes'
import Helper from '../../helpers/Helper'
import FlowUIManager, { IFlowChange } from '../../managers/FlowUIManager'
import NodeManager from '../../managers/NodeManager'
import ValidationManager from '../../managers/ValidationManager'
import { modals } from '../services/ModalService'
import NodeDryRunConfig from './NodeDryRunConfig'
import NodesIcon from './nodes/NodesIcon'
import NodeEditor from './nodes_editor/NodeEditor'
import NodeShapeConfig from './NodeShapeConfig'

interface P {
    defaultSelected: INode
    nodeErrors: string[]
    setNodeErrors?: (errors: string[]) => void
    flow: IFlow
    applyFlowChange?: (change: IFlowChange) => void
    onClose: () => void
    isLoading?: boolean
    mode: 'read' | 'edit'
    approval: IApprovalRequest
}

const NodeDataEditor = ({ defaultSelected, nodeErrors, flow, setNodeErrors, applyFlowChange, onClose, isLoading, mode, approval }: P) => {
    const theme = useTheme()
    const [selected, setSelected] = useState<INode>(defaultSelected)
    const [openErrorDialog, setOpenErrorDialog] = useState(false)
    const [openCollapse, setOpenCollapse] = useState(false)
    const description = NodeManager.getDescription(selected.type)
    const hasModified = Helper.stableStringify(defaultSelected) !== Helper.stableStringify(selected)
    const canEdit = approval.status === 'draft' || approval.status === 'rejected'

    const errorName = FlowUIManager.isUniqueName(flow, selected)

    const onShortcut = (e: KeyboardEvent) => {
        if (mode === 'edit' || canEdit) {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault()
                handleSave()
            }
        }
    }

    const handleDeleteNode = async () => {
        if (mode === 'edit' || canEdit) {
            if (selected) {
                modals.openConfirm({
                    title: 'Delete node',
                    children: <Typography>{`Are you sure to delete the node "${selected.name}"?`}</Typography>,
                    labels: { confirm: 'Delete', cancel: 'Cancel' },
                    colors: { confirm: 'error', cancel: 'primary' },
                    onConfirm: () => {
                        const deletedNode = FlowUIManager.updateNodes([{ type: 'remove', id: selected.uuid }], flow)
                        applyFlowChange(deletedNode)
                        setSelected(null)
                        onClose()
                    },
                    onCancel: () => onClose()
                })
            }
        }
    }

    const handleSave = async () => {
        if (mode === 'edit' || canEdit) {
            if (!selected) return
            const change = FlowUIManager.updateNode(selected, flow)
            applyFlowChange(change)
            const errors = ValidationManager.validateNode(selected)
            setNodeErrors(errors)
        }
    }

    useEffect(() => {
        if (mode === 'edit' || canEdit) {
            document.addEventListener('keydown', onShortcut)
            return () => document.removeEventListener('keydown', onShortcut)
        }
    }, [handleSave])

    return (
        <div style={{
            height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column',
            borderRadius: mode === 'read' ? '12px' : ''
        }}>
            <div style={{ position: 'sticky', top: 0, backgroundColor: theme.palette.background.default, padding: theme.spacing(3) }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'calc(var(--unit) * 2)' }}>
                    <NodesIcon nodeType={selected.type} size={40} />
                    <div style={{ flex: 1 }}>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: errorName ? 'error' : 'dimgray'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: errorName ? 'error' : 'dimgray'
                                    },
                                    '& input': {
                                        padding: 'var(--unit)'
                                    }
                                }
                            }}
                            variant='outlined'
                            defaultValue={selected.name}
                            error={errorName}
                            helperText={errorName && 'Node name must be unique'}
                            disabled={mode === 'read' || !canEdit}
                            onChange={e => {
                                const newName = e.target.value
                                setSelected({ ...selected, name: newName })
                            }}
                        />
                        <Typography paddingLeft={1} variant='body2' color='lightgray' textTransform='capitalize'>{description.group}</Typography>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {mode === 'edit' && (
                            <>
                                <Tooltip title='Save node' arrow>
                                    <div>
                                        <IconButton disabled={isLoading || errorName || !canEdit} onClick={handleSave}>
                                            <Badge badgeContent={hasModified ? 1 : 0} color='warning' variant='dot'>
                                                {isLoading ? <CircularProgress size={24} /> : <SaveIcon />}
                                            </Badge>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                                <Tooltip title='Add node description' arrow>
                                    <div>
                                        <IconButton disabled={isLoading || errorName || !canEdit} onClick={() => setOpenCollapse(!openCollapse)}>
                                            <AddCommentOutlined />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                                <Tooltip title='Show node error' arrow>
                                    <div>
                                        <IconButton disabled={isLoading || nodeErrors.length === 0 || !canEdit} onClick={() => setOpenErrorDialog(true)}>
                                            <Badge badgeContent={nodeErrors.length} color='error'>
                                                <ErrorIcon />
                                            </Badge>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title='Close drawer' arrow>
                            <IconButton disabled={isLoading} onClick={onClose}>
                                <CloseRounded />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
                        <DialogTitle>Node Errors</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {nodeErrors.map((error, index) => (
                                    <div key={index}>{index + 1}) {error}</div>
                                ))}
                            </DialogContentText>
                        </DialogContent>
                    </Dialog>
                </div>
                <Collapse in={openCollapse} timeout='auto' unmountOnExit>
                    <Box paddingTop={2}>
                        <TextField
                            fullWidth
                            variant='outlined'
                            multiline
                            maxRows={4}
                            value={selected?.description ?? ''}
                            onChange={e => setSelected({ ...selected, description: e.target.value })}
                        />
                    </Box>
                </Collapse>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: theme.spacing(3), backgroundColor: theme.palette.background.paper, overflow: 'auto' }}>
                <NodeEditor selectedNode={selected} flow={flow} setSelected={setSelected} disable={mode === 'read' || !canEdit} onClose={onClose} />
                {mode === 'edit' && (
                    <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%' sx={{ paddingTop: 3, justifySelf: 'end', alignSelf: 'end' }}>
                        <Box display='flex' flexDirection='row' alignItems='center'>
                            <NodeDryRunConfig flow={flow} selectedNode={selected} setSelected={setSelected} approval={approval} />
                            <NodeShapeConfig flow={flow} selectedNode={selected} setSelected={setSelected} approval={approval} />
                        </Box>
                        <Button
                            startIcon={<DeleteOutlineIcon />}
                            variant='outlined'
                            disabled={isLoading || !canEdit}
                            onClick={handleDeleteNode}
                            color='error'>
                            delete node
                        </Button>
                    </Box>
                )}
            </div>
        </div>
    )
}

export default NodeDataEditor
